/**
 * DatasetCombobox - single-select dataset picker backed by server-side search.
 *
 * Replaces the `<Select>` pickers that rendered one page of datasets and gave no
 * hint that the rest existed (#1026): on a production catalog of ~1,143 datasets
 * they offered 20 (the API default) or 100, so most datasets were unreachable.
 *
 * Search runs on the server — the dataset list endpoint pushes `search` down into
 * SQL — and the query is deferred until the popover opens, so mounting a form
 * costs no catalog read at all.
 */
import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDataset, useDatasets } from '@/hooks/useDatasets';
import { cn } from '@/lib/utils';
import type { Dataset } from '@/types';

/** Datasets requested per search. One small page keeps the picker responsive. */
const PAGE_SIZE = 20;

/** Debounce applied to typing before a search request goes out, in ms. */
const SEARCH_DEBOUNCE_MS = 300;

export interface DatasetComboboxProps {
  /** Currently selected dataset, identified by whichever field `valueField` names. */
  value?: string | null;
  /**
   * Which dataset field the caller stores. Defaults to `'id'`; `JoinBuilder`
   * stores `name`, because a join target is addressed by dataset name.
   */
  valueField?: 'id' | 'name';
  /** Receives the new value and the full row (both null when the selection is cleared). */
  onChange: (value: string | null, dataset: Dataset | null) => void;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Label for an explicit "no dataset" choice. The option is omitted when undefined. */
  emptyOptionLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const DatasetCombobox: React.FC<DatasetComboboxProps> = ({
  value,
  valueField = 'id',
  onChange,
  placeholder = 'Select a dataset...',
  emptyOptionLabel,
  disabled = false,
  id,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError } = useDatasets(
    { page: 1, size: PAGE_SIZE, search: debouncedSearch || undefined },
    // Nothing is fetched until the user opens the picker.
    { enabled: open }
  );

  const datasets: Dataset[] = data?.items ?? [];
  const total = data?.total ?? 0;

  // A `name`-valued selection is its own label. An `id`-valued one has to be
  // resolved, because the selected dataset is usually absent from the current
  // search page — an edit form loads a selection made long ago.
  const needsLookup = valueField === 'id' && !!value;
  const { data: selectedDataset } = useDataset(needsLookup ? value! : '');
  const selectedLabel = value
    ? valueField === 'name'
      ? value
      : (selectedDataset?.name ?? datasets.find((ds) => ds.id === value)?.name ?? value)
    : null;

  const handleSelect = (dataset: Dataset) => {
    onChange(valueField === 'name' ? dataset.name : dataset.id, dataset);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null, null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* `var(...)` spelled out: Tailwind v4 reads a bare `[--x]` as a literal,
          not a variable, so `w-[--radix-popover-trigger-width]` emits no rule at
          all and the popover shrinks to its content. */}
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        {/* Results are already filtered by the server; cmdk's own filtering would
            re-filter them and hide valid matches. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search datasets..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading datasets...
              </div>
            )}

            {isError && (
              <div className="py-6 text-center text-sm text-destructive">
                Failed to load datasets. Please try again.
              </div>
            )}

            {!isLoading && !isError && (
              <>
                {datasets.length === 0 && (
                  <CommandEmpty>
                    {debouncedSearch ? 'No datasets match your search.' : 'No datasets available.'}
                  </CommandEmpty>
                )}

                {emptyOptionLabel && (
                  <CommandItem value="__clear__" onSelect={handleClear}>
                    <Check
                      className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')}
                    />
                    <span className="text-muted-foreground">{emptyOptionLabel}</span>
                  </CommandItem>
                )}

                {datasets.map((dataset) => {
                  const optionValue = valueField === 'name' ? dataset.name : dataset.id;
                  return (
                    <CommandItem
                      key={dataset.id}
                      value={optionValue}
                      onSelect={() => handleSelect(dataset)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === optionValue ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="truncate">{dataset.name}</span>
                    </CommandItem>
                  );
                })}

                {/* Without this, a page-sized result set looks like the whole
                    catalog — the silent truncation this component exists to fix. */}
                {total > datasets.length && (
                  <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                    Showing {datasets.length} of {total} — refine your search
                  </div>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DatasetCombobox;
